import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Button } from '@fragments';
import { IconExternalLink, IconCircleCheck, IconCircleX, IconArrowsHorizontal, IconX } from '@tabler/icons-react';
import { getChargebackReason } from '../../../enums/chargebackReasons';
import './styles.scss';

const RESOLVED_STATUSES = ['won', 'lost', 'closed', 'reversed'];

// Status → `chargebacks.alert.resolved.*`, resolved from the caller's `t`.
const getResolvedMessages = (t) => ({
    won: {
        title: t('chargebacks.alert.resolved.won.title'),
        description: t('chargebacks.alert.resolved.won.description'),
    },
    lost: {
        title: t('chargebacks.alert.resolved.lost.title'),
        description: t('chargebacks.alert.resolved.lost.description'),
    },
    reversed: {
        title: t('chargebacks.alert.resolved.reversed.title'),
        description: t('chargebacks.alert.resolved.reversed.description'),
    },
    closed: {
        title: t('chargebacks.alert.resolved.closed.title'),
        description: t('chargebacks.alert.resolved.closed.description'),
    },
});

const getUnderReviewMessage = (t) => ({
    title: t('chargebacks.alert.underReview.title'),
    description: t('chargebacks.alert.underReview.description'),
});

export default function ContestationAlert({ reason, status, onAccept, onDispute }) {
    const { t } = useTranslation('banking');
    const isResolved = RESOLVED_STATUSES.includes(status);
    const isUnderReview = status === 'under_review';
    const resolvedMessages = useMemo(() => getResolvedMessages(t), [t]);
    const resolvedMessage = resolvedMessages[status];
    const reasonData = useMemo(() => getChargebackReason(t, reason), [t, reason]);

    const activeMessage = isUnderReview
        ? getUnderReviewMessage(t)
        : { title: t('chargebacks.alert.activeTitle'), description: reasonData.description };

    const title = isResolved && resolvedMessage ? resolvedMessage.title : activeMessage.title;
    const bodyText = isResolved && resolvedMessage ? resolvedMessage.description : activeMessage.description;

    // Instructions only apply while a dispute can still be filed — not once the
    // evidence is under review, nor after the case is resolved.
    const instructionsText = !isResolved && !isUnderReview ? reasonData.instructions : null;

    const titleIcon = {
        won: <IconCircleCheck />,
        lost: <IconCircleX />,
        reversed: <IconArrowsHorizontal />,
        closed: <IconX />,
        under_review: <IconCircleCheck />,
    }[isUnderReview ? 'under_review' : 'lost'];

    return (
        <div className={`contestation-alert contestation-alert--${isResolved ? status : 'active'}`}>
            <div className="contestation-alert__body">
                <Text.h4 weight={400} left={titleIcon}>{title}</Text.h4>
                <div style={{ paddingLeft: '26px', paddingTop: '6px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Text.span contrast={5} >
                        {bodyText}
                    </Text.span>
                    {instructionsText && (
                        <Text.span contrast={5}>
                            {instructionsText}
                        </Text.span>
                    )}
                </div>
            </div>

            <div className="contestation-alert__divider" />

            <div className="contestation-alert__footer">
                <a
                    href="https://help.ginastee.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contestation-alert__help-link"
                >
                    <Text.span>{t('chargebacks.alert.helpLink')}</Text.span>
                    <IconExternalLink size={13} />
                </a>

                {['requested', 'waiting_documents'].includes(status) && (
                    <div className="contestation-alert__actions">
                        <Button
                            text={t('chargebacks.alert.dispute')}
                            color="primary"
                            size="small"
                            onClick={onDispute}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
